import { Subscription } from 'rxjs'
import { Observable } from 'rxjs/internal/Observable'
import { filter, switchMap } from 'rxjs/operators'

import { AfterViewInit, Component, OnDestroy } from '@angular/core'
import { Project } from '@classes/Project'
import { Scene } from '@classes/Scene'
import { AuthenticationService } from '@services/authentication.service'
import { AwsService } from '@services/aws.service'
import { DashboardService } from '@services/dashboard.service'
import { ModalService } from '@services/modal.service'
import { ProjectService, usersProjects$ } from '@services/project.service'
import { SceneService } from '@services/scene.service'
import { SubscriptionService } from '@services/subscription.service'
import { ThumbnailService } from '@services/thumbnail.service'
import { TooltipService } from '@services/tooltip.service'

@Component({
  selector: 'shared-modal-project-details',
  templateUrl: './project-details.component.html',
  styleUrls: ['./project-details.component.scss'],
  standalone: false
})
export class ProjectDetailsComponent implements OnDestroy, AfterViewInit {
  public cancelText = 'Close'
  get modal() { return this.dashboardService.detailsModal }
  set modal(modal) { this.dashboardService.detailsModal = modal }
  public title = 'Project Details'

  public onCancel: CallableFunction
  public link: string
  public kartoriumLogo = "assets/kartorium_logo_dark.svg"

  private _subscriptions: Subscription[] = []
  public currentUserPermission: string = ''
  public selectedProject$: Observable<Project> = this.dashboardService.selectedProjectForDetails$

  public showUpload = false
  private project: Project
  private scenes: Scene[]

  get canMakeProjectPublic() { return !this.inFreeTier }
  get firstSceneThumbnail() { return this.scenes?.find(scene => scene.previousID == undefined)?.thumbnail }
  get hasOrganization() { return this._authenticationService.hasOrganization }
  get inFreeTier() { return this._subscriptionService.inFreeTier }

  constructor(
    private _authenticationService: AuthenticationService,
    private _awsService: AwsService,
    private _sceneService: SceneService,
    private _subscriptionService: SubscriptionService,
    public _thumbnailService: ThumbnailService,
    private _tooltipService: TooltipService,
    public dashboardService: DashboardService,
    public modalService: ModalService,
    public projectService: ProjectService,
  ) {
    this._subscriptions.push(
      this.dashboardService.selectedProjectForDetails$.pipe(
        filter(project => project !== undefined && project.permissions.length > 0),
        switchMap(project => {
          this.project = project
          if (this.dashboardService.getUserPermissionForProject(project)!=='Viewer') void this._thumbnailService.policy.load('project',project.id).catch(()=>undefined)
          this.currentUserPermission = this.dashboardService.getUserPermissionForProject(project)
          return this._sceneService.getScenes(project.id)
        })
      ).subscribe(scenes => {
        this.scenes = scenes
        if (!this.thumbSameAsScene(this.project)) this.showUpload = true
      }),
    )
  }

  _submit() {

  }

  _cancel() {
    if (this.onCancel) {
      this.onCancel()
    }
    if (this.dashboardService.selectedProjectForDetails.title.length == 0) return
    this.modal?.hide()
  }

  deleteProject() {
    this.dashboardService.openDeleteProjectModal(this.project)
    this.modal.hide()
  }

  updateProject(updatedProject: Project) {
    if (updatedProject.title.length == 0) return
    this.projectService.updateProject(updatedProject).subscribe()
  }

  thumbSameAsScene(project: Project) {
    return !project.thumbnail || project.thumbnail == this.firstSceneThumbnail
  }

  async clearThumbnail() {
    try {
      await this._thumbnailService.policy.useAutomatic('project',this.project.id)
      this.showUpload=false
    } catch { this._thumbnailService.policy.view('project',this.project.id).status='error' }
  }

  convertImageToBase64(file: File): Promise<{ base64String: string; dimensions: { width: number; height: number } }> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const img = new Image()
        img.onload = () => {
          const base64String = reader.result?.toString() || ''
          resolve({ base64String, dimensions: { width: img.width, height: img.height } })
        }
        img.onerror = (error) => reject(error)

        if (reader.result) {
          img.src = reader.result.toString() // Set the source of the image to the Base64 data URL
        } else {
          reject('File could not be read')
        }
      }
      reader.onerror = (error) => reject(error)
      reader.readAsDataURL(file) // Read the file as a Base64-encoded data URL
    })
  }

  async addThumbnail(file: File) {
    if(!file) return
    try {
      const {base64String,dimensions}=await this.convertImageToBase64(file)
      const cropped=await this._thumbnailService.cropScreenshot(base64String,dimensions.width,dimensions.height)
      const blob=await (await fetch(cropped)).blob()
      const preview=await this._thumbnailService.policy.saveUserImage('project',this.project.id,{
        file:new File([blob],file.name,{type:blob.type||'image/png'}),preview:cropped,
      })
      if(preview) {
        this.project.thumbnail=preview
        this.projectService.updateUsersProjectsLocally(this.project)
        this.projectService.updateOrganizationProjectsLocally(this.project)
      }
    } catch { this._thumbnailService.policy.view('project',this.project.id).status='error' }
  }

  ngAfterViewInit(): void {
    setTimeout(() => this._tooltipService.intializeTooltips())
  }

  ngOnDestroy() {
    this.modal.dispose()
    this._subscriptions.forEach(subscription => subscription.unsubscribe())
  }
}